import React, { useEffect, useState } from 'react';
import { makeRequest } from "../../utils/httpRequest";
import { useRecoilState } from "recoil";
import { loadingAtom } from "../../atoms";
import Config from "../../core/config";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";import { useNavigate } from "react-router-dom";
import {getCompanyById, isUserAdmin, fetchCompanyGroups, findCompaniesByQueryString} from "../../utils/requestUtils";
import { UserPermission } from "../../utils/utils";
import Loading from "../../core/Loading";
import { brazilianStates } from "../../utils/utils";
import { searchCEP } from "../../core/Cep";
import BackButton from "../common/BackButton";
import { usePermission } from "../../core/PermissionContext";

const apiUrl = Config.apiUrl;

/** ISO-8601: 1 = segunda … 7 = domingo; vazio = todos os dias */
const WORK_ACCESS_WEEKDAY_OPTIONS = [
    { value: '', label: 'Todos os dias' },
    { value: '1', label: 'Segunda-feira' },
    { value: '2', label: 'Terça-feira' },
    { value: '3', label: 'Quarta-feira' },
    { value: '4', label: 'Quinta-feira' },
    { value: '5', label: 'Sexta-feira' },
    { value: '6', label: 'Sábado' },
    { value: '7', label: 'Domingo' },
];

const CompanyManager = () => {
    const [, setLoading] = useRecoilState(loadingAtom);
    const { hasUserPermission, loadingPermissions } = usePermission();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    const newEntity = {
        id: undefined,
        name: '',
        fantasyName: '',
        address: '',
        addressNumber: '',
        addressComplement: '',
        neighborhood: '',
        cep: '',
        city: '',
        state: '',
        phone: '',
        email: '',
        cpfCnpj: '',
        inscricaoEstadual: '',
        rentalContractFileName: '',
        rentalContracts: [],
        associatedCompanies: [],
        apiKeyDosePrintWebSocketPrinter: '',
        accessTokenMercadoPago: '',
        logoPath: '',
        permissions: [],
        password: '',
        workScheduleZoneId: 'America/Sao_Paulo',
        workAccessPeriods: [
            { startTime: '08:00', endTime: '12:00', dayOfWeek: null },
            { startTime: '13:00', endTime: '17:00', dayOfWeek: null },
        ],
    };

    const [formData, setFormData] = useState(newEntity);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [allCompanies, setAllCompanies] = useState([]);
    const [newRentalContractName, setNewRentalContractName] = useState('');
    const [newRentalContractFileName, setNewRentalContractFileName] = useState('');
    const [isCopying, setIsCopying] = useState(false);
    // Add this state for select all
    const allPermissionsSelected = availablePermissions.length > 0 && availablePermissions.every((permission) => formData.permissions.includes(permission.permission));
    const handleSelectAllPermissions = (e) => {
        const checked = e.target.checked;
        setFormData((prevData) => ({
            ...prevData,
            permissions: checked ? availablePermissions.map(p => p.permission) : []
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePermissionChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            permissions: checked
                ? [...prevData.permissions, value]
                : prevData.permissions.filter(permission => permission !== value)
        }));
    };

    const handleStateChange = (idState) => {
        setFormData((prevData) => ({
            ...prevData,
            state: idState,
        }));
    };

    const handleAssociatedCompaniesChange = (event) => {
        const selected = Array.from(event.target.selectedOptions, option => parseInt(option.value, 10));
        setFormData(prevData => ({
            ...prevData,
            associatedCompanies: selected
        }));
    };

    const handleAddRentalContract = () => {
        const trimmedName = newRentalContractName.trim();
        const trimmedFileName = newRentalContractFileName.trim();

        if (!trimmedFileName) {
            return;
        }

        setFormData(prevData => ({
            ...prevData,
            rentalContracts: [
                ...(prevData.rentalContracts || []),
                {
                    id: null,
                    name: trimmedName,
                    fileName: trimmedFileName,
                }
            ]
        }));

        setNewRentalContractName('');
        setNewRentalContractFileName('');
    };

    const handleRentalContractChange = (index, field, value) => {
        setFormData(prevData => {
            const rentalContracts = [...(prevData.rentalContracts || [])];
            if (!rentalContracts[index]) {
                return prevData;
            }

            rentalContracts[index] = {
                ...rentalContracts[index],
                [field]: value,
            };

            return {
                ...prevData,
                rentalContracts,
            };
        });
    };

    const handleRemoveRentalContract = (index) => {
        setFormData(prevData => ({
            ...prevData,
            rentalContracts: (prevData.rentalContracts || []).filter((_, i) => i !== index),
        }));
    };

    const handleWorkAccessPeriodChange = (index, field, value) => {
        setFormData(prevData => {
            const periods = [...(prevData.workAccessPeriods || [])];
            if (!periods[index]) {
                return prevData;
            }
            periods[index] = { ...periods[index], [field]: value };
            return { ...prevData, workAccessPeriods: periods };
        });
    };

    const handleAddWorkAccessPeriod = () => {
        setFormData(prevData => ({
            ...prevData,
            workAccessPeriods: [...(prevData.workAccessPeriods || []), { startTime: '09:00', endTime: '18:00', dayOfWeek: null }],
        }));
    };

    const handleRemoveWorkAccessPeriod = (index) => {
        setFormData(prevData => ({
            ...prevData,
            workAccessPeriods: (prevData.workAccessPeriods || []).filter((_, i) => i !== index),
        }));
    };

    async function fetchPermissions() {
        try {
            setLoading(true);
            const response = await makeRequest(apiUrl + '/api/companypermission/companies_permission_info', 'GET');

            setAvailablePermissions(response);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        } finally {
            setLoading(false);
        }
    }


    async function makeQuery() {
        if (id) {
            try {
                setLoading(true);
                const response = await getCompanyById(id);
                const normalizedRentalContracts = (response.rentalContracts && response.rentalContracts.length > 0)
                    ? response.rentalContracts.map(contract => ({
                        id: contract.id ?? null,
                        name: contract.name || '',
                        fileName: contract.fileName || '',
                        companyId: contract.companyId ?? null,
                    }))
                    : (response.rentalContractFileName
                        ? [{
                            id: null,
                            name: '',
                            fileName: response.rentalContractFileName,
                            companyId: response.id ?? null,
                        }]
                        : []);
                const defaultPeriods = [
                    { startTime: '08:00', endTime: '12:00', dayOfWeek: null },
                    { startTime: '13:00', endTime: '17:00', dayOfWeek: null },
                ];
                const normalizedResponse = {
                    ...response,
                    associatedCompanies: (response.associatedCompanies || []).filter(companyId => companyId !== response.id),
                    rentalContracts: normalizedRentalContracts,
                    rentalContractFileName: normalizedRentalContracts[0]?.fileName || '',
                    workScheduleZoneId: response.workScheduleZoneId || 'America/Sao_Paulo',
                    workAccessPeriods: Array.isArray(response.workAccessPeriods) && response.workAccessPeriods.length > 0
                        ? response.workAccessPeriods.map((p) => ({
                            startTime: (p.startTime || '').toString().slice(0, 5),
                            endTime: (p.endTime || '').toString().slice(0, 5),
                            dayOfWeek: p.dayOfWeek != null && p.dayOfWeek !== '' ? Number(p.dayOfWeek) : null,
                        }))
                        : defaultPeriods,
                };
                setFormData(normalizedResponse);
                if (normalizedResponse.groupPermissions && normalizedResponse.groupPermissions.id) {
                    setSelectedGroupId(normalizedResponse.groupPermissions.id);
                } else {
                    setSelectedGroupId(null);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        } else if (!isCopying) {
            setFormData(newEntity);
            setSelectedGroupId(null);
        }
    }

    const [companyGroups, setCompanyGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(undefined);
    const [permissionFilter, setPermissionFilter] = useState('');

    useEffect(() => {
        isUserAdmin().then(userAdmin => {
            setIsAdmin(userAdmin);
            if (userAdmin) {
                fetchCompanyGroups().then(setCompanyGroups);
            }
        });
    }, []);

    useEffect(() => {
        async function loadCompanies() {
            try {
                const companies = await findCompaniesByQueryString('');
                setAllCompanies(companies || []);
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        }

        loadCompanies();
    }, []);

    useEffect(() => {
        fetchPermissions();
    }, []);

    useEffect(() => {
        if (!isCopying) {
            makeQuery();
        }
        setIsCopying(false);
    }, [id]);

    useEffect(() => {
        if (formData.groupPermissions && formData.groupPermissions.id) {
            setSelectedGroupId(formData.groupPermissions.id);
        }
    }, [formData.groupPermissions]);

    useEffect(() => {
        if (!loadingPermissions && isAdmin !== undefined) {
            if (id && !isAdmin && !hasUserPermission(UserPermission.EDIT_COMPANY)) {
                navigate('/unauthorizedaccess');
            } else if (!id && !isAdmin && !hasUserPermission(UserPermission.CREATE_COMPANY)) {
                navigate('/unauthorizedaccess');
            }
        }
    }, [loadingPermissions, hasUserPermission, id, navigate, isAdmin]);

    const handleGroupChange = (e) => {
        const groupId = e.target.value ? parseInt(e.target.value) : null;
        setSelectedGroupId(groupId);
        const group = companyGroups.find(g => g.id === groupId);
        if (group) {
            // Aplicar automaticamente todas as permissões do grupo à empresa
            setFormData(prev => ({ 
                ...prev, 
                groupPermissions: group, 
                permissions: group.permissions 
            }));
        } else {
            setFormData(prev => ({ ...prev, groupPermissions: null }));
        }
    };

    // Filtrar permissões baseado no texto de busca
    const getFilteredPermissions = () => {
        if (!selectedGroupId) return [];
        
        const groupPerms = companyGroups.find(g => g.id === selectedGroupId)?.permissions || [];
        const availablePerms = availablePermissions.filter(perm => groupPerms.includes(perm.permission));

        return availablePerms.filter(permission =>
            permission.description.toLowerCase().includes(permissionFilter.toLowerCase()) ||
            permission.permission.toLowerCase().includes(permissionFilter.toLowerCase())
        );
    };

    const filteredPermissions = getFilteredPermissions();
    const associatedCompanyOptions = allCompanies.filter(company => company.id !== formData.id);

    const onConfirm = async () => {
        try {
            if (validateInputs()) {
                setLoading(true);
                await new Promise(resolve => setTimeout(resolve, 300));
                await saveEntity();
                navigate(`/${isAdmin ? 'admin' : 'app'}/companylist`);
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Error ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    async function searchCepAndFillAddressInputs() {
        let result = undefined;
        setLoading(true);
        try {
            result = await searchCEP(formData.cep);
        } finally {
            setLoading(false);
        }
        if (result != null) {
            setFormData((prevData) => ({
                ...prevData,
                address: result.street,
                neighborhood: result.neighborhood,
                city: result.city,
                state: result.state,
            }));
        } else {
            alert('CEP não encontrado.');
        }
    }

    function validateInputs() {
        if (!formData.name || formData.name === '') {
            alert('Preencha o campo nome.');
            return false;
        }
        if (!formData.fantasyName || formData.fantasyName === '') {
            alert('Preencha o campo Nome Fantasia.');
            return false;
        }
        if (!formData.address || formData.address === '') {
            alert('Preencha o campo endereço.');
            return false;
        }
        if (!formData.neighborhood || formData.neighborhood === '') {
            alert('Preencha o campo bairro.');
            return false;
        }
        if (!formData.cep || formData.cep === '') {
            alert('Preencha o campo cep.');
            return false;
        }
        if (!formData.city || formData.city === '') {
            alert('Preencha o campo cidade.');
            return false;
        }
        if (!formData.state || formData.state === '') {
            alert('Preencha o campo estado.');
            return false;
        }
        if (!formData.phone || formData.phone === '') {
            alert('Preencha o campo telefone.');
            return false;
        }
        if (!formData.cpfCnpj || formData.cpfCnpj === '') {
            alert('Preencha o campo CPF/CNPJ.');
            return false;
        }
        if (!formData.inscricaoEstadual || formData.inscricaoEstadual === '') {
            alert('Preencha o campo Inscrição Estadual.');
            return false;
        }
        if (!selectedGroupId) {
            alert('Selecione um grupo de permissões.');
            return false;
        }
        return true;
    }

    async function saveEntity() {
        const url = apiUrl + '/api/companies';
        setLoading(true);
        try {
            // Prepare payload with groupPermissions as an object with id (if selected)
            const sanitizedAssociatedCompanies = Array.from(new Set(
                (formData.associatedCompanies || [])
                    .map(id => parseInt(id, 10))
                    .filter(id => !isNaN(id) && id !== formData.id)
            ));

            const sanitizedRentalContracts = (formData.rentalContracts || [])
                .map(contract => {
                    const sanitizedContract = {
                        ...(contract.id ? { id: contract.id } : {}),
                        name: contract.name ? contract.name.trim() : '',
                        fileName: contract.fileName ? contract.fileName.trim() : '',
                    };

                    if (contract.companyId) {
                        sanitizedContract.companyId = contract.companyId;
                    } else if (formData.id) {
                        sanitizedContract.companyId = formData.id;
                    }

                    return sanitizedContract;
                })
                .filter(contract => contract.fileName !== '');

            const payload = {
                ...formData,
                associatedCompanies: sanitizedAssociatedCompanies,
                rentalContracts: sanitizedRentalContracts,
                rentalContractFileName: sanitizedRentalContracts[0]?.fileName || '',
                groupPermissions: selectedGroupId ? { id: selectedGroupId } : null,
            };
            await makeRequest(url, id ? 'PUT' : 'POST', {
                'Content-Type': 'application/json',
            }, JSON.stringify(payload));
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const handleCopy = () => {
        setIsCopying(true);

        const copiedRentalContracts = (formData.rentalContracts || [])
            .filter(contract => contract && contract.fileName && contract.fileName.trim() !== '')
            .map(contract => ({
                id: null,
                name: contract.name || '',
                fileName: contract.fileName,
            }));

        const copiedFormData = {
            ...formData,
            id: undefined,
            name: formData.name ? `CÓPIA - ${formData.name}` : 'CÓPIA',
            rentalContracts: copiedRentalContracts,
            rentalContractFileName: copiedRentalContracts[0]?.fileName || '',
            workAccessPeriods: [...(formData.workAccessPeriods || [])],
        };

        setFormData(copiedFormData);
        setSelectedGroupId(formData.groupPermissions?.id || null);
        setNewRentalContractName('');
        setNewRentalContractFileName('');

        const url = new URL(window.location.href);
        url.searchParams.delete('id');
        window.history.replaceState({}, '', url.toString());

        window.scrollTo(0, 0);
    };

    return (
        <div>
            <div style={{display: 'flex', alignItems: 'center'}}>
                <div className="header-label">{formData.id > 0 ? 'Editar' : 'Cadastrar'} Empresa</div>
                {formData.id > 0 && (
                    <button onClick={handleCopy} style={{marginLeft: '10px', marginRight: '10px'}}>
                        Copiar
                    </button>
                )}
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Razão Social:</div>
                    <input type="text" style={{width: '300px'}} value={formData.name} name="name"
                           onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Nome Fantasia:</div>
                    <input type="text" style={{width: '300px'}} value={formData.fantasyName} name="fantasyName"
                           onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>CPF/CNPJ:</div>
                    <input style={{width: '135px'}} type="text" value={formData.cpfCnpj} name="cpfCnpj"
                           maxLength="18"
                           onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>Inscrição Estadual:</div>
                    <input style={{width: '140px'}} type="text" value={formData.inscricaoEstadual}
                           name="inscricaoEstadual"
                           maxLength="18"
                           onChange={handleChange}/>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '80px'}}>CEP:</div>
                    <div>
                        <input style={{width: '80px'}} type="text" value={formData.cep} name="cep"
                               onChange={handleChange}/>
                        <button onClick={searchCepAndFillAddressInputs}>Buscar CEP</button>
                    </div>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Endereço:</div>
                    <input type="text" style={{width: '297px'}} value={formData.address} name="address"
                           onChange={handleChange} autoComplete={"off"}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '70px'}}>Número:</div>
                    <input type="number" step="1" placeholder="0" style={{width: '70px'}}
                           value={formData.addressNumber} name="addressNumber"
                           onChange={handleChange} autoComplete={"off"}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Complemento:</div>
                    <input type="text" style={{width: '310px'}} value={formData.addressComplement}
                           name="addressComplement"
                           onChange={handleChange} autoComplete={"off"}/>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Bairro:</div>
                    <input type="text" style={{width: '400px'}} value={formData.neighborhood}
                           name="neighborhood" onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Cidade:</div>
                    <input type="text" style={{width: '290px'}} value={formData.city}
                           name="city" onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Estado:</div>
                    <select value={formData.state} onChange={(e) => handleStateChange(e.target.value)}>
                        <option value="">Selecione...</option>
                        {brazilianStates.map((estado) => (
                            <option key={estado.sigla} value={estado.sigla}>
                                {estado.sigla} - {estado.nome}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Telefone:</div>
                    <input type="text" value={formData.phone} name="phone" onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Email:</div>
                    <input type="text" style={{width: '300px'}} value={formData.email} name="email"
                           onChange={handleChange}/>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center', width: '100%'}}>
                    <div>Companhias Associadas:</div>
                    <select
                        multiple
                        style={{width: '100%', minHeight: '140px'}}
                        value={(formData.associatedCompanies || []).map(companyId => companyId.toString())}
                        onChange={handleAssociatedCompaniesChange}
                    >
                        {associatedCompanyOptions.map(company => (
                            <option key={company.id} value={company.id}>{company.name}</option>
                        ))}
                    </select>
                    <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                        Use Ctrl ou Shift para selecionar várias empresas.
                    </div>
                </div>
            </div>
            <div style={{marginTop: '5px'}}>
                <div>Contratos de Locação:</div>
                <div style={{marginTop: '5px'}}>
                    {formData.rentalContracts && formData.rentalContracts.length > 0 ? (
                        formData.rentalContracts.map((contract, index) => (
                            <div key={contract.id ?? `rental-contract-${index}`} style={{display: 'flex', alignItems: 'center', marginBottom: '5px'}}>
                                <input
                                    type="text"
                                    style={{flex: '0 0 30%', marginRight: '5px'}}
                                    value={contract.name || ''}
                                    onChange={(e) => handleRentalContractChange(index, 'name', e.target.value)}
                                    placeholder="Nome do contrato (opcional)"
                                />
                                <input
                                    type="text"
                                    style={{flex: 1, marginRight: '5px'}}
                                    value={contract.fileName || ''}
                                    onChange={(e) => handleRentalContractChange(index, 'fileName', e.target.value)}
                                    placeholder="Arquivo de contrato"
                                />
                                <button type="button" onClick={() => handleRemoveRentalContract(index)}>Remover</button>
                            </div>
                        ))
                    ) : (
                        <div style={{color: '#888'}}>Nenhum contrato cadastrado.</div>
                    )}
                </div>
                <div style={{display: 'flex', marginTop: '10px', alignItems: 'center'}}>
                    <input
                        type="text"
                        style={{flex: '0 0 30%', marginRight: '5px'}}
                        value={newRentalContractName}
                        onChange={(e) => setNewRentalContractName(e.target.value)}
                        placeholder="Nome do contrato (opcional)"
                    />
                    <input
                        type="text"
                        style={{flex: 1, marginRight: '5px'}}
                        value={newRentalContractFileName}
                        onChange={(e) => setNewRentalContractFileName(e.target.value)}
                        placeholder="Arquivo de contrato"
                    />
                    <button type="button" onClick={handleAddRentalContract}>Adicionar</button>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center', width: '100%'}}>
                    <div>API Key Dose Print WebSocket Printer:</div>
                    <input type="text" style={{width: '100%'}} value={formData.apiKeyDosePrintWebSocketPrinter} name="apiKeyDosePrintWebSocketPrinter"
                           onChange={handleChange}/>
                </div>
            </div>
            {hasUserPermission(UserPermission.ENABLE_MERCADO_PAGO) && (
                <div style={{display: 'flex'}}>
                    <div style={{marginTop: '5px', alignItems: 'center', width: '100%'}}>
                        <div>Access Token Mercado Pago:</div>
                        <input type="text" style={{width: '100%'}} value={formData.accessTokenMercadoPago} name="accessTokenMercadoPago"
                               onChange={handleChange}/>
                    </div>
                </div>
            )}
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center', width: '100%'}}>
                    <div>Caminho da Logomarca:</div>
                    <input type="text" style={{width: '100%'}} value={formData.logoPath} name="logoPath"
                           onChange={handleChange}/>
                </div>
            </div>
            <div style={{ marginTop: '15px', border: '1px solid #ddd', padding: '12px', borderRadius: '6px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Horário de acesso ao sistema</div>
                <div style={{ marginTop: '8px' }}>
                    <div style={{ width: '220px', marginBottom: '4px' }}>Fuso horário (ex.: America/Sao_Paulo):</div>
                    <input
                        type="text"
                        style={{ width: '100%', maxWidth: '400px' }}
                        name="workScheduleZoneId"
                        value={formData.workScheduleZoneId || ''}
                        onChange={handleChange}
                    />
                </div>
                <div style={{ marginTop: '10px' }}>Períodos permitidos:</div>
                <div style={{ marginTop: '6px' }}>
                    {(formData.workAccessPeriods || []).map((period, index) => (
                        <div key={`work-period-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', gap: '8px', flexWrap: 'wrap' }}>
                            <select
                                aria-label="Dia da semana"
                                value={period.dayOfWeek == null || period.dayOfWeek === '' ? '' : String(period.dayOfWeek)}
                                onChange={(e) => handleWorkAccessPeriodChange(
                                    index,
                                    'dayOfWeek',
                                    e.target.value === '' ? null : parseInt(e.target.value, 10),
                                )}
                            >
                                {WORK_ACCESS_WEEKDAY_OPTIONS.map((o) => (
                                    <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            <input
                                type="time"
                                value={(period.startTime || '').toString().slice(0, 5)}
                                onChange={(e) => handleWorkAccessPeriodChange(index, 'startTime', e.target.value)}
                            />
                            <span>até</span>
                            <input
                                type="time"
                                value={(period.endTime || '').toString().slice(0, 5)}
                                onChange={(e) => handleWorkAccessPeriodChange(index, 'endTime', e.target.value)}
                            />
                            <button type="button" onClick={() => handleRemoveWorkAccessPeriod(index)}>Remover</button>
                        </div>
                    ))}
                </div>
                <button type="button" style={{ marginTop: '8px' }} onClick={handleAddWorkAccessPeriod}>Adicionar período</button>
            </div>
            {isAdmin && (
                <div style={{ marginTop: '5px' }}>
                    <div style={{ width: '180px' }}>Grupo de Permissões:</div>
                    <select data-test="group-select" value={selectedGroupId || ''} onChange={handleGroupChange}>
                        <option value=''>Selecione um grupo</option>
                        {companyGroups.map(group => (
                            <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                    </select>
                </div>
            )}
            
            <div style={{ marginTop: '15px' }}>
                <div style={{ width: '180px', marginBottom: '10px' }}>Permissões:</div>
                
                {/* Campo de filtro */}
                {selectedGroupId && (
                    <div style={{ marginBottom: '10px' }}>
                        <input
                            type="text"
                            placeholder="Filtrar permissões..."
                            value={permissionFilter}
                            onChange={(e) => setPermissionFilter(e.target.value)}
                            style={{ width: '300px', padding: '5px' }}
                        />
                    </div>
                )}
                {selectedGroupId && filteredPermissions.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                        <input
                            type="checkbox"
                            checked={filteredPermissions.every(p => formData.permissions.includes(p.permission))}
                            onChange={e => {
                                const groupPerms = companyGroups.find(g => g.id === selectedGroupId)?.permissions || [];
                                setFormData(prev => ({
                                    ...prev,
                                    permissions: e.target.checked ? groupPerms : []
                                }));
                            }}
                        />
                        <span style={{ marginLeft: 8 }}>Selecionar/Desmarcar todas</span>
                    </div>
                )}
                
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ccc', padding: '10px' }}>
                    {selectedGroupId ? (
                        filteredPermissions.length > 0 ? (
                            filteredPermissions.map(permission => {

                                const perm = availablePermissions.find(p => p.permission === permission.permission);
                                if (!perm) return null;
                                return (
                                    <div key={perm.permission} style={{ display: "flex", alignItems: "center", marginBottom: '5px' }}>
                                        <label style={{ display: "flex", alignItems: "center" }}>
                                            <input
                                                type="checkbox"
                                                value={perm.permission}
                                                checked={formData.permissions.includes(perm.permission)}
                                                onChange={handlePermissionChange}
                                            />
                                            <span style={{ marginLeft: '8px' }}>
                                                {perm.description}
                                            </span>
                                        </label>
                                    </div>
                                );
                            })
                        ) : (
                            <div style={{ color: '#888', textAlign: 'center' }}>
                                {permissionFilter ? 'Nenhuma permissão encontrada com o filtro aplicado.' : 'Nenhuma permissão disponível no grupo selecionado.'}
                            </div>
                        )
                    ) : (
                        <div style={{ color: '#888', textAlign: 'center' }}>Selecione um grupo para ver as permissões.</div>
                    )}
                </div>
            </div>
            <ActionButtonsManagerView>
                <BackButton />
                <ConfirmButton onClick={onConfirm}/>
            </ActionButtonsManagerView>
        </div>
    );
};

export default CompanyManager;
